<template>
    <div class="w-full bg-white rounded-lg relative">      
      <div
        @drop.prevent="onDrop"
        @dragover.prevent="isDragActive = true"
        @dragleave.prevent="isDragActive = false"
        @click="triggerFileInput"
        :class="[
          'border-2 border-gray-600 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
          isDragActive ? 'border-primary bg-primary/5' : 'border-stroke-strong',
          isLoading ? 'pointer-events-none opacity-50' : ''
        ]"
      >
        <input
          ref="fileInput"
          type="file"
          multiple
          :accept="acceptedTypes.join(',')"
          @change="handleFileSelect"
          class="hidden"
          data-testid="browse-files"
        />
        <div class="flex flex-col items-center justify-center">
          <div class="rounded-full p-2 mb-2" style="background-color: #F5FBF9; height: 37px; width: 37px;">
            <UploadIcon class="h-6 w-6" style="color: #9BD6C6;" />
          </div>
          <p class="text-md font-semibold text-gray-900 mb-1">
            {{ isDragActive ? 'Drop files here' : 'Drag and drop files here' }}
          </p>
          <p class="text-xs text-gray-600 mb-1">
            Maximum file size is 10MB
          </p>
          <p class="text-xs text-gray-600 mb-3">
            Files types accepted PDF, JPEG, .docx, PNG
          </p>
          <vs-button
            type="border"
            class="mt-2 shadow rounded-md font-semibold"
            style="border-color: #E5E7EB; color: #0E4233;"
          >
            Browse files
          </vs-button>
        </div>
      </div>
      
      <div v-if="error" class="text-xs text-red-500 mt-2 text-left">
        {{ error }}
      </div>
      
      <div v-if="extractedFiles.length > 0" class="mt-6 flex flex-col">
        <div
          v-for="(file, indextr) in extractedFiles"
          :key="`${file.name}-${file.lastModified}-${indextr}`"
          class="flex items-center justify-between bg-white px-4 py-3 rounded-lg mb-1 border"
          style="border-color: #E5E7EB; box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);"
        >
          <div class="flex items-center flex-1">
            <div class="w-6 h-6 flex items-center justify-center mr-3">
              <ImageIcon
                v-if="file.type.startsWith('image/')"
                class="w-5 h-5 text-gray-500"
              />
              <FileTextIcon v-else class="w-5 h-5 text-gray-500" />
            </div>
            <div class="flex flex-col">
              <span class="text-sm font-medium text-gray-900 truncate" style="max-width: 200px;">
                {{ file.name }}
              </span>
              <span class="text-xs text-gray-500">
                {{ formatFileSize(file.size) }}
              </span>
            </div>
          </div>
          <div class="flex items-center ml-4">
            <button
              type="button"
              class="p-1 rounded hover:bg-gray-100 transition-colors bg-transparent border-transparent"
              @click="removeFile(file)"
              aria-label="Remove file"
            >
              <XIcon class="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>
  
<script>
import { UploadIcon, ImageIcon, FileTextIcon,  XIcon } from 'vue-feather-icons';
  
export default {
  name: 'FileUpload',
  components: {
    UploadIcon,
    ImageIcon,
    FileTextIcon,
    XIcon,
  },
  props: {
    acceptFileTypes: {
      type: Array,
      default: () => [
        { type: 'application/pdf', label: 'PDF' },
        { type: 'image/jpeg', label: 'JPEG' },
        { type: 'image/png', label: 'PNG' },
        { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', label: '.docx' },
      ],
    },
    files: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDragActive: false,
      error: null,
    };
  },
  computed: {
    acceptedTypes() {
      return this.acceptFileTypes.map(f => f.type);
    },
    acceptedLabels() {
      return this.acceptFileTypes.map(f => f.label).join(', ');
    },
    extractedFiles() {
      return this.files.map(fileObj => fileObj.file);
    },
  },
  methods: {
    validateFiles(files) {
      const acceptedExtensions = ['.pdf', '.jpeg', '.jpg', '.docx', '.png'];
      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

      for (const file of files) {
        const extension = '.' + file.name.split('.').pop().toLowerCase();
        const validExtension = acceptedExtensions.includes(extension);
        const validMimeType = this.acceptedTypes.includes(file.type);

        if (!validExtension || !validMimeType) {
          this.error = `Only ${acceptedExtensions.join(', ')} files are accepted.`;
          return false;
        }

        if (file.size > MAX_FILE_SIZE) {
          this.error = `Each file must be ${MAX_FILE_SIZE / (1024 * 1024)}MB or less.`;
          return false;
        }
      }

      return true;
    },
      
    onDrop(event) {
      this.isDragActive = false;
      const droppedFiles = Array.from(event.dataTransfer.files);
      if (this.validateFiles(droppedFiles)) {
        const existingFiles = this.files.map(f => f.file);
        const allFiles = [...existingFiles, ...droppedFiles];
        
        this.$emit('update:files', allFiles);
        this.error = null;
      }
    },
      
    handleFileSelect(event) {
      const selectedFiles = Array.from(event.target.files);

      if (this.validateFiles(selectedFiles)) {
        const existingFiles = this.files.map(f => f.file);
        const allFiles = [...existingFiles, ...selectedFiles];
        
        this.$emit('update:files', allFiles);
        this.error = null;
      }
        
      event.target.value = '';
    },
      
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
      
    removeFile(fileToRemove) {
      const updatedFiles = this.files.filter(file => file.file !== fileToRemove);
      this.$emit('remove:files', updatedFiles);
    },
      
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    },
      
    handleDragOver(event) {
      event.preventDefault();
      this.isDragActive = true;
    },
      
    handleDragLeave(event) {
      event.preventDefault();
      this.isDragActive = false;
    },
  },
  mounted() {
    document.addEventListener('dragover', this.handleDragOver);
    document.addEventListener('dragleave', this.handleDragLeave);
  },
  beforeDestroy() {
    document.removeEventListener('dragover', this.handleDragOver);
    document.removeEventListener('dragleave', this.handleDragLeave);
  },
};
</script>